import { apiClient } from './apiClient';
import { 
  Patient, 
  PatientFormData, 
  PatientFilters, 
  PatientListResponse 
} from '../types/patient';

// API base URL for future use
// const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

export const patientService = {
  /**
   * Obtener lista de pacientes con filtros y paginación
   */
  async getPatients(
    page: number = 1,
    limit: number = 10,
    filters: PatientFilters = {}
  ): Promise<PatientListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Agregar filtros solo si tienen valor
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get(`/patients?${params}`);
    return response.data;
  },

  /**
   * Obtener paciente por ID
   */
  async getPatientById(id: number): Promise<Patient> {
    const response = await apiClient.get(`/patients/${id}`);
    return response.data;
  },

  /**
   * Crear nuevo paciente
   */
  async createPatient(patientData: PatientFormData): Promise<{ message: string; patient: Patient }> {
    const response = await apiClient.post('/patients', patientData);
    return response.data;
  },

  /**
   * Actualizar paciente existente
   */
  async updatePatient(id: number, patientData: Partial<PatientFormData>): Promise<{ message: string; patient: Patient }> {
    const response = await apiClient.put(`/patients/${id}`, patientData);
    return response.data;
  },

  /**
   * Eliminar paciente (soft delete)
   */
  async deletePatient(id: number): Promise<{ message: string; patientId: number }> {
    const response = await apiClient.delete(`/patients/${id}`);
    return response.data;
  },

  /**
   * Validar RUT chileno
   */
  validateRut(rut: string): boolean {
    // Formato básico: XX.XXX.XXX-X
    const rutRegex = /^[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9kK]$/;
    if (!rutRegex.test(rut)) {
      return false;
    }

    // Validar dígito verificador
    const cleanRut = rut.replace(/\./g, '').replace(/-/g, '');
    const body = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1).toUpperCase();

    let sum = 0;
    let multiplier = 2;

    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }

    const expectedDv = 11 - (sum % 11);
    let expectedDvStr;

    if (expectedDv === 11) expectedDvStr = '0';
    else if (expectedDv === 10) expectedDvStr = 'K';
    else expectedDvStr = expectedDv.toString();

    return dv === expectedDvStr;
  },

  /**
   * Validar teléfono chileno
   */
  validatePhone(phone: string): boolean {
    if (!phone || phone.trim() === '') return true; // Opcional
    
    // Formato chileno flexible: +56 seguido de 9 o 2 y 8-9 dígitos
    const phoneRegex = /^\+56\s?[29][0-9]{8,9}$/;
    return phoneRegex.test(phone);
  },

  /**
   * Validar fecha de nacimiento
   */
  validateDateOfBirth(date: string): boolean {
    const birthDate = new Date(date);
    const today = new Date();
    
    if (birthDate >= today) return false;
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 3 && age <= 120;
  },

  /**
   * Validar formulario completo
   */
  validateForm(data: PatientFormData): Record<string, string> {
    const errors: Record<string, string> = {};

    // Validaciones obligatorias
    if (!data.firstName.trim()) {
      errors.firstName = 'El nombre es obligatorio';
    } else if (data.firstName.length < 2) {
      errors.firstName = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!data.lastName.trim()) {
      errors.lastName = 'El apellido es obligatorio';
    } else if (data.lastName.length < 2) {
      errors.lastName = 'El apellido debe tener al menos 2 caracteres';
    }

    if (!data.dateOfBirth) {
      errors.dateOfBirth = 'La fecha de nacimiento es obligatoria';
    } else if (!this.validateDateOfBirth(data.dateOfBirth)) {
      errors.dateOfBirth = 'La fecha de nacimiento no es válida (edad entre 3 y 120 años)';
    }

    if (!data.gender) {
      errors.gender = 'El género es obligatorio';
    }

    if (!data.identificationNumber.trim()) {
      errors.identificationNumber = 'El RUT es obligatorio';
    } else if (!this.validateRut(data.identificationNumber)) {
      errors.identificationNumber = 'El RUT no es válido';
    }

    if (!data.evaluatorId) {
      errors.evaluatorId = 'El evaluador es obligatorio';
    }

    // Validaciones opcionales
    if (data.phone && !this.validatePhone(data.phone)) {
      errors.phone = 'El teléfono debe ser chileno: +56 seguido de 9 o 2 y 8-9 dígitos';
    }

    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'El email no es válido';
    }

    if (data.address && data.address.length > 500) {
      errors.address = 'La dirección no puede exceder 500 caracteres';
    }

    return errors;
  }
}; 